import { useContext, useMemo, type Context } from "react";
import { type Store } from "./common";
import {
  type ConsumerMutationConfig,
  type ConsumerMutationProp,
  type ConsumerMutationResult,
  type ConsumerSetterConfig,
  type ConsumerSetterResult,
  type ParametersExceptFirst,
  type StoreRequest,
  type StoreValue,
} from "./types";
import { useBaseGetters } from "./useGetters";

export function makeUseConsumerMutations<
  TStoreBaseValue,
  TConsumerSetterConfig extends ConsumerSetterConfig<TStoreBaseValue>,
  TConsumerMutationConfig extends ConsumerMutationConfig<TStoreBaseValue, TConsumerSetterConfig>,
>(
  context: Context<Store<StoreValue<TStoreBaseValue>>>,
  useConsumerSetters: () => ConsumerSetterResult<TStoreBaseValue, TConsumerSetterConfig>,
  mutationConfig: TConsumerMutationConfig,
) {
  return function useConsumerMutations<
    const TConsumerMutationProp extends ConsumerMutationProp<
      TStoreBaseValue,
      TConsumerSetterConfig,
      TConsumerMutationConfig
    >[],
  >(mutationProps: TConsumerMutationProp) {
    const consumerSetters = useConsumerSetters();
    const store = useContext(context);

    const mutations: {
      name: keyof TConsumerMutationConfig;
      fn: StoreRequest & (() => Promise<any>);
      id: string;
    }[] = useMemo(
      () =>
        mutationProps.map((mutationName) => {
          const mutationId = (mutationName as string) + "_" + crypto.randomUUID();
          const mutationConfigFn = mutationConfig[mutationName];
          async function mutate(...args: ParametersExceptFirst<typeof mutationConfigFn>) {
            store.set(
              (storeValue) =>
                (storeValue.idToRequest[mutationId] = {
                  status: "loading",
                  error: undefined,
                }),
            );
            try {
              const result = await mutationConfigFn(
                { get: () => store.get().base, ...consumerSetters },
                ...args,
              );
              store.set(
                (storeValue) =>
                  (storeValue.idToRequest[mutationId] = {
                    status: "success",
                    error: undefined,
                  }),
              );
              return result;
            } catch (e) {
              store.set(
                (storeValue) =>
                  (storeValue.idToRequest[mutationId] = {
                    status: "error",
                    error: e instanceof Error ? e : new Error("Unknown error"),
                  }),
              );
              throw e;
            }
          }
          mutate.status = "success" as const;
          return {
            name: mutationName,
            id: mutationId,
            fn: mutate,
          };
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [store],
    );

    const mutationStoreRequests = useBaseGetters(
      store,
      mutations.map(
        (mutation) => (storeValue: StoreValue<TStoreBaseValue>) =>
          storeValue.idToRequest[mutation.id] ?? { status: "idle" },
      ),
    );

    return mutationStoreRequests.reduce((acc, mutationRequest, midx) => {
      mutations[midx].fn.status = mutationRequest.status;
      mutations[midx].fn.error = mutationRequest.error;
      return {
        ...acc,
        [mutations[midx].name]: mutations[midx].fn,
      };
    }, {}) as ConsumerMutationResult<
      TStoreBaseValue,
      TConsumerSetterConfig,
      TConsumerMutationConfig,
      TConsumerMutationProp
    >;
  };
}
